import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { FormattedMessage, injectIntl } from "react-intl";
import TurnableCard from "../Management/Pacients/Dashboard/TurnableCard";
import PatientGeneralCardView from "../Management/Pacients/Dashboard/PatientGeneralCardView";
import { Pacient } from "app/models/pacient";
import PatientLPPCardView from "../Management/Pacients/Dashboard/PatientLPPCardView";
import PatientMonitoringCardView from "../Management/Pacients/Dashboard/PatientMonitoringCardView";
import PatientEvaluationsCardView from "../Management/Pacients/Dashboard/PatientEvaluationsCardView";
import PatientIndicatorOccurrencesCardView from "../Management/Pacients/Dashboard/PatientIndicatorOccurrencesCardView";
import { Monitoring } from "app/models/monitoring";
import {
  getAllPatientsWithFilters,
  GetAllPatientsWithFiltersResponse,
  updatePatientProfilePhoto,
  deletePatientProfilePhoto,
} from "api/pacients";
import ReactPaginate from "react-paginate";
import { toAbsoluteUrl } from "_metronic/_helpers";
import SVG from "react-inlinesvg";
import { getMonitoring } from "api/monitoring";
import { Modal, Button as BootstrapButton } from "react-bootstrap";
import { ModalYesNo } from "../Globals/ModalYesNo";

function PatientDashboard() {
  const [dashboardData, setDashboardData] = useState({
    patients: {
      loading: true,
      totalCount: 0,
      data: [] as Pacient[],
    },
    monitoring: {
      map: {} as Record<
        string,
        {
          loading: boolean;
          data: Monitoring | null;
        }
      >, // Key = Patient ID
    },
  });

  const [filters, setFilters] = useState({
    name: "", // Patient name filter
    limit: 5, // Number of patients per page
    offset: 0, // Offset for pagination
  });

  const [pendingName, setPendingName] = useState("");
  const [patientsCache, setPatientsCache] = useState<
    Record<string, GetAllPatientsWithFiltersResponse>
  >({});

  // Fetch patients based on filters
  const fetchPatients = async () => {
    const { name, limit, offset } = filters;

    // Check if data for the current offset + name is already cached
    const cacheKey = `${offset}-${name}`;
    if (patientsCache[cacheKey]) {
      setDashboardData((prev) => ({
        ...prev,
        patients: {
          ...prev.patients,
          loading: false,
          data: patientsCache[cacheKey].patients,
          totalCount: patientsCache[cacheKey].total,
        },
      }));
      return;
    }

    try {
      setDashboardData((prev) => ({
        ...prev,
        patients: {
          ...prev.patients,
          loading: true,
        },
      }));

      const response = await getAllPatientsWithFilters({ name, limit, offset });

      // Cache the results for the current offset + name
      const newCacheKey = `${offset}-${name}`;
      setPatientsCache((prev) => ({
        ...prev,
        [newCacheKey]: response,
      }));

      // Update the total count and data
      setDashboardData((prev) => ({
        ...prev,
        patients: {
          loading: false,
          totalCount: response.total,
          data: response.patients,
        },
      }));
    } catch (error) {
      console.error("Error fetching patients:", error);
      setDashboardData((prev) => ({
        ...prev,
        patients: {
          ...prev.patients,
          loading: false,
          data: [],
        },
      }));
    }
  };

  const handlePageChange = (page: { selected: number }) => {
    const newOffset = page.selected * filters.limit;
    setFilters((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const handleNameInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPendingName(event.target.value);
  };

  const handleSearchClick = () => {
    setFilters((prev) => ({
      ...prev,
      name: pendingName,
      offset: 0, // Reset to the first page when applying a new filter
    }));
  };

  useEffect(() => {
    fetchPatients();
  }, [filters]);

  const [
    profilePhotoUpdatedModalOptions,
    setProfilePhotoUpdatedModalOptions,
  ] = useState({
    show: false,
    title: "",
    message: "",
    error: false,
  });

  const [
    confirmDeletePhotoModalOptions,
    setConfirmDeletePhotoModalOptions,
  ] = useState({
    showSignal: 0,
    patientId: "",
    photoExtension: "",
  });

  return (
    <Card>
      <Card.Header style={{ display: "flex", flexWrap: "wrap" }}>
        <div
          style={{
            marginRight: "auto",
            fontWeight: 500,
            fontSize: "1.275rem",
            color: "#181C32",
          }}
        >
          <FormattedMessage id="PATIENTS_DASHBOARD.PAGE.TITLE" />
        </div>
        <div
          className="inputSearch"
          style={{
            display: "flex",
            width: "40%",
            gap: 5,
          }}
        >
          <div className="input-group-prepend">
            <button
              type="button"
              className="input-group-text bg-white"
              onClick={handleSearchClick}
            >
              <span className="svg-icon svg-icon-sm svg-icon-primary">
                <SVG
                  src={toAbsoluteUrl("/media/svg/icons/General/Search.svg")}
                />
              </span>
            </button>
          </div>
          <input
            type="text"
            value={pendingName}
            onChange={handleNameInputChange}
            className="form-control border-left-10"
            placeholder="Procurar Paciente por Nome"
          />
        </div>
      </Card.Header>
      <Card.Body>
        <div
          style={{
            display: "flex",
            gap: 60,
            flexWrap: "wrap",
          }}
        >
          {dashboardData.patients.loading ? (
            <div>Carregando dados...</div>
          ) : dashboardData.patients.data.length > 0 ? (
            dashboardData.patients.data.map((patient) => (
              <div key={patient.id} style={{ height: 300, width: 300 }}>
                <TurnableCard
                  slots={[
                    {
                      id: "personal-data",
                      name: "Dados Pessoais",
                      component: (
                        <PatientGeneralCardView
                          patient={patient}
                          onUpdatePhoto={(patientId, photo) => {
                            const photoExtension = photo.type.split("/")[1];
                            updatePatientProfilePhoto(patientId, photo)
                              .then((photoUrl) => {
                                setDashboardData((prev) => ({
                                  ...prev,
                                  patients: {
                                    ...prev.patients,
                                    data: prev.patients.data.map((p) =>
                                      p.id === patientId
                                        ? new Pacient({
                                            ...p,
                                            photoUrl,
                                            photoExtension,
                                          })
                                        : p
                                    ),
                                  },
                                }));

                                setProfilePhotoUpdatedModalOptions({
                                  show: true,
                                  title:
                                    "Sucesso - Atualização de Foto de Perfil",
                                  message:
                                    "Foto de perfil atualizada com sucesso.",
                                  error: false,
                                });
                              })
                              .catch((error) => {
                                console.error(
                                  "Error updating patient photo:",
                                  error
                                );

                                setProfilePhotoUpdatedModalOptions({
                                  show: true,
                                  title: "Erro - Atualização de Foto de Perfil",
                                  message: error.message,
                                  error: true,
                                });
                              });
                          }}
                          onDeletePhoto={(patientId) => {
                            setConfirmDeletePhotoModalOptions((prev) => ({
                              ...prev,
                              showSignal: 1,
                              patientId,
                              photoExtension: patient.photoExtension!,
                            }));
                          }}
                        />
                      ),
                    },
                    {
                      id: "monitoring",
                      name: "Monitoramento",
                      component: (
                        <PatientMonitoringCardView
                          patient={patient}
                          monitoring={
                            dashboardData.monitoring.map[patient.id]?.data
                          }
                          loading={
                            dashboardData.monitoring.map[patient.id] !==
                            undefined
                              ? dashboardData.monitoring.map[patient.id].loading
                              : true
                          }
                        />
                      ),
                    },
                    {
                      id: "pressure-injury",
                      name: "Lesão por Pressão",
                      component: (
                        <PatientLPPCardView
                          patient={patient}
                          monitoring={
                            dashboardData.monitoring.map[patient.id]?.data
                          }
                          loading={
                            dashboardData.monitoring.map[patient.id] !==
                            undefined
                              ? dashboardData.monitoring.map[patient.id].loading
                              : true
                          }
                        />
                      ),
                    },
                    {
                      id: "evaluations",
                      name: "Avaliações",
                      component: (
                        <PatientEvaluationsCardView patient={patient} />
                      ),
                    },
                    {
                      id: "indicator-occurrences",
                      name: "Indicadores",
                      component: (
                        <PatientIndicatorOccurrencesCardView
                          patient={patient}
                        />
                      ),
                    },
                  ]}
                  onSlotChange={(slotId) => {
                    if (
                      slotId === "monitoring" ||
                      slotId === "pressure-injury"
                    ) {
                      // If monitoring data was already fetched, return
                      if (
                        dashboardData.monitoring.map[patient.id] !== undefined
                      ) {
                        return;
                      }

                      // If monitoring ID is not set, set loading to false, monitoring data to null and return
                      if (!patient.monitoringId) {
                        setDashboardData((prev) => ({
                          ...prev,
                          monitoring: {
                            ...prev.monitoring,
                            map: {
                              ...prev.monitoring.map,
                              [patient.id]: {
                                loading: false,
                                data: null,
                              },
                            },
                          },
                        }));

                        return;
                      }

                      // Fetch monitoring data from the API
                      getMonitoring(patient.monitoringId)
                        .then((response) => {
                          setDashboardData((prev) => ({
                            ...prev,
                            monitoring: {
                              ...prev.monitoring,
                              map: {
                                ...prev.monitoring.map,
                                [patient.id]: {
                                  loading: false,
                                  data: response,
                                },
                              },
                            },
                          }));
                        })
                        .catch((error) => {
                          console.error("Error fetching monitoring:", error);
                          // Set loading to false and monitoring data to null on API error
                          setDashboardData((prev) => ({
                            ...prev,
                            monitoring: {
                              ...prev.monitoring,
                              map: {
                                ...prev.monitoring.map,
                                [patient.id]: {
                                  loading: false,
                                  data: null,
                                },
                              },
                            },
                          }));
                        });
                    }
                  }}
                />
              </div>
            ))
          ) : (
            <div>Nenhum dado a ser mostrado.</div>
          )}
        </div>
        {dashboardData.patients.totalCount > 0 && (
          <div
            style={{
              marginTop: "4rem",
            }}
          >
            <ReactPaginate
              pageCount={Math.ceil(
                dashboardData.patients.totalCount / filters.limit
              )}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handlePageChange}
              containerClassName="pagination"
              forcePage={filters.offset / filters.limit}
              activeClassName="active"
              nextLabel={
                <span className="svg-icon svg-icon-md svg-icon-primary">
                  <SVG
                    src={toAbsoluteUrl(
                      "/media/svg/icons/Navigation/Angle-right.svg"
                    )}
                  />
                </span>
              }
              previousLabel={
                <span className="svg-icon svg-icon-md svg-icon-primary">
                  <SVG
                    src={toAbsoluteUrl(
                      "/media/svg/icons/Navigation/Angle-left.svg"
                    )}
                  />
                </span>
              }
              disabledClassName="disabled"
            />
          </div>
        )}
      </Card.Body>

      <Modal
        size="lg"
        show={profilePhotoUpdatedModalOptions.show}
        onHide={() => {
          setProfilePhotoUpdatedModalOptions((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{profilePhotoUpdatedModalOptions.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{profilePhotoUpdatedModalOptions.message}</Modal.Body>
        <Modal.Footer>
          <BootstrapButton
            variant={
              profilePhotoUpdatedModalOptions.error ? "danger" : "primary"
            }
            onClick={() => {
              setProfilePhotoUpdatedModalOptions((prev) => ({
                ...prev,
                show: false,
              }));
            }}
          >
            {profilePhotoUpdatedModalOptions.error ? (
              <FormattedMessage id="CLOSE" />
            ) : (
              <FormattedMessage id="OK" />
            )}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <ModalYesNo
        showSignal={confirmDeletePhotoModalOptions.showSignal}
        titleId="PATIENTS_DASHBOARD.MODAL.DELETE_PROFILE_PHOTO.TITLE"
        bodyTextId="PATIENTS_DASHBOARD.MODAL.DELETE_PROFILE_PHOTO.BODY"
        onYesClicked={() => {
          deletePatientProfilePhoto(
            confirmDeletePhotoModalOptions.patientId,
            confirmDeletePhotoModalOptions.photoExtension
          )
            .then(() => {
              setDashboardData((prev) => ({
                ...prev,
                patients: {
                  ...prev.patients,
                  data: prev.patients.data.map((p) =>
                    p.id === confirmDeletePhotoModalOptions.patientId
                      ? new Pacient({ ...p, photoUrl: null })
                      : p
                  ),
                },
              }));
            })
            .catch((error) => {
              console.error("Error deleting patient photo:", error);
            });
        }}
        onClosed={() => {
          setConfirmDeletePhotoModalOptions((prev) => ({
            ...prev,
            showSignal: 0,
          }));
        }}
      />
    </Card>
  );
}

export default injectIntl(PatientDashboard);
